import { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Header } from '@components/molecules/Header';
import { Footer } from '@components/molecules/Footer';
import { Container } from '@components/atoms/Container';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Button, ButtonMode } from '@components/atoms/Button';
import { SpaceCard } from '@components/molecules/SpaceCard';
import { ToggleButton } from '@components/atoms/ToggleButton';
import { Select, SelectType, SimpleOptionsList } from '@components/atoms/Select';
import { MapContainer, Marker, TileLayer } from 'react-leaflet';
import { icon } from 'leaflet';
import mapPinIcon from '@assets/images/map-pin.svg';
import locationIcon from '@assets/icons/location.svg';
import analyticsIcon from '@assets/icons/analytics.svg';
import networkIcon from '@assets/icons/network.svg';
import interfaceIcon from '@assets/icons/interface.svg';
import { useData } from '@hooks/useData';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useGetFeatures } from '@services/useGetFeatures';
import { useGetSpaces } from '@services/useGetSpaces';
import { useGetBannerReadyToBoost } from '@services/useGetBannerReadyToBoost';
import { useGetSpacesCoordinates } from '@services/useGetSpacesCoordinates';
import { useGetBannerHero } from '@services/useGetBannerHero';
import { useGetSpaceTypes } from '@services/useGetSpaceTypes';
import styles from './Home.module.css';
import { MapHeader } from './MapHeader';
import { MapBoundsUpdater } from './MapBoundsUpdater';
import { useGetBannerInfo } from '@services/useGetBannerInfo';
import { GallerySection } from '@components/organisms/GallerySection/GallerySection';
import { CtaBanner } from '@components/organisms/CtaBanner';

const pinIcon = icon({
    iconUrl: mapPinIcon,
    iconSize: [38, 95],
    shadowSize: [50, 64],
    iconAnchor: [22, 94],
    shadowAnchor: [4, 62],
    popupAnchor: [-3, -76],
});

export const Home: FC = () => {
    const { t } = useTranslation();
    const { navigate } = useCustomNavigate();
    const { spaces, isLoadingSpaces } = useData();
    const { features, request: fetchFeatures, loading: featuresLoading } = useGetFeatures();
    const { request: fetchSpaces } = useGetSpaces();
    const { request: fetchGetBannerInfo, data: bannerInfo, loading: bannerInfoLoading } = useGetBannerInfo();

    const { banner, request: fetchBannerReadyToBoost, loading: bannerLoading } = useGetBannerReadyToBoost();
    const { coordinates, request: fetchSpacesCoordinates, loading: coordinatesLoading } = useGetSpacesCoordinates();
    const { hero, request: fetchBannerHero, loading: heroLoading } = useGetBannerHero();
    const { spaceTypes, request: fetchSpaceTypes, loading: spaceTypesLoading } = useGetSpaceTypes();
    const [searchType, setSearchType] = useState<string>('vender');
    const [spaceType, setSpaceType] = useState<string>('');
    const [mapSearchType, setMapSearchType] = useState<string>('vender');
    const [mapSpaceType, setMapSpaceType] = useState<string>('');

    const translations = {
        toggleSell: t('home-component.toggle.sell'),
        toggleRent: t('home-component.toggle.rent'),
        defaultSpace: t('home-component.select.default-space'),
        propertyType: t('home-component.select.property-type'),
        search: t('home-component.button.search'),
        highlights: t('home-component.section.highlights'),
        recentOpportunities: t('home-component.section.recent-opportunities'),
        mapLoading: t('home-component.map.loading'),
        bannerReadyToBoostAlt: t('home-component.banner.ready-to-boost-alt'),
    };

    const handleBannerClick = () => {
        if (banner?.content_banner_ready_to_boost_link) {
            navigate(banner.content_banner_ready_to_boost_link);
        }
    };

    const handleSearch = () => {
        const queryParams = new URLSearchParams();

        // Mapear searchType para featuredType (sale/rent)
        const featuredType = searchType === 'vender' ? 'sale' : 'rent';
        queryParams.append('featuredType', featuredType);

        // Adicionar spaceType UUID se selecionado
        if (spaceType) {
            queryParams.append('spaceTypeUuid', spaceType);
        }

        // Navegar para /spaces com os parâmetros
        navigate(`/spaces?${queryParams.toString()}`);
    };

    const handleMapSearch = () => {
        const featuredType = mapSearchType === 'vender' ? 'sale' : 'rent';

        fetchSpacesCoordinates({
            featuredType,
            spaceTypeUuid: mapSpaceType || undefined,
        });
    };

    useEffect(() => {
        fetchSpaceTypes();
        fetchGetBannerInfo();
        fetchFeatures();
        fetchSpaces({
            page: 1,
            perPage: 4,
            orderBy: 'created_at',
            sortDesc: true,
        });
        fetchBannerReadyToBoost();
        fetchBannerHero();
        fetchSpacesCoordinates({
            featuredType: 'sale',
        });
    }, []);

    const propertyTypeOptions: SimpleOptionsList = [
        { value: '', text: translations.propertyType },
        ...spaceTypes.map((type) => ({
            value: type.uuid,
            text: type.name,
        })),
    ];

    return (
        <>
            <Header variant="transparent" />
            <div className={styles.home}>
                {!heroLoading && hero && (
                    <section
                        className={styles.heroSection}
                        style={hero.content_banner_hero_image ? { backgroundImage: `url(${hero.content_banner_hero_image})` } : undefined}
                    >
                        <Container classes='p-0'>
                            <div className={styles.heroContent}>
                                <div className={styles.heroTextGroup}>
                                    {hero.content_banner_hero_title && (
                                        <Typography
                                            tagVariant={TypographyTagVariants.h1}
                                            fontSize="46"
                                            fontWeight="900"
                                            color="light"
                                            classes={styles.heroTitle}>
                                            {hero.content_banner_hero_title}
                                        </Typography>
                                    )}

                                    {hero.content_banner_hero_description && (
                                        <Typography
                                            tagVariant={TypographyTagVariants.p}
                                            fontSize="18"
                                            fontWeight="900"
                                            color="light"
                                            classes={styles.heroSubtitle}>
                                            {hero.content_banner_hero_description}
                                        </Typography>
                                    )}
                                </div>

                            <div className={styles.searchBox}>
                                <div className={styles.toggleGroup}>
                                    <ToggleButton
                                        name="searchType"
                                        value="vender"
                                        label={translations.toggleSell}
                                        checked={searchType === 'vender'}
                                        onChange={setSearchType}
                                        classes="rounded-0"
                                    />
                                    <ToggleButton
                                        name="searchType"
                                        value="arrendar"
                                        label={translations.toggleRent}
                                        checked={searchType === 'arrendar'}
                                        onChange={setSearchType}
                                        classes="rounded-0"
                                    />
                                </div>

                                <div className={styles.selectWrapper}>
                                    <Select
                                        type={SelectType.variant1}
                                        options={[
                                            { value: '', text: translations.defaultSpace },
                                            ...spaceTypes.map(type => ({
                                                value: type.uuid,
                                                text: type.name
                                            }))
                                        ]}
                                        value={spaceType}
                                        onChange={setSpaceType}
                                        classes={styles.selectInput}
                                        disabled={spaceTypesLoading}
                                    />
                                </div>

                                <Button mode={ButtonMode.primary} label={translations.search} classes={styles.searchButton} onClick={handleSearch} />
                            </div>
                        </div>
                    </Container>
                </section>
                )}

                {!featuresLoading && features && (
                    <section className={styles.featuresSection}>
                        <Container>
                            <div className={styles.featuresGrid}>
                                <div className={styles.featureCard}>
                                    <div className={styles.featureIcon}>
                                        <img 
                                            src={features.content_features_item_1_icon || locationIcon} 
                                            alt={features.content_features_item_1_title} 
                                            width="74" 
                                            height="74" 
                                        />
                                    </div>
                                    <div className={styles.featureContent}>
                                        <Typography fontSize="20" fontWeight="900" color="dark" classes={styles.featureTitle}>
                                            {features.content_features_item_1_title}
                                        </Typography>
                                        <Typography fontSize="16" fontWeight="400" classes={styles.featureDescription}>
                                            {features.content_features_item_1_description}
                                        </Typography>
                                    </div>
                                </div>
                                <div className={styles.featureCard}>
                                    <div className={styles.featureIcon}>
                                        <img 
                                            src={features.content_features_item_2_icon || analyticsIcon} 
                                            alt={features.content_features_item_2_title} 
                                            width="74" 
                                            height="74" 
                                        />
                                    </div>
                                    <div className={styles.featureContent}>
                                        <Typography fontSize="20" fontWeight="900" color="dark" classes={styles.featureTitle}>
                                            {features.content_features_item_2_title}
                                        </Typography>
                                        <Typography fontSize="16" fontWeight="400" classes={styles.featureDescription}>
                                            {features.content_features_item_2_description}
                                        </Typography>
                                    </div>
                                </div>
                                <div className={styles.featureCard}>
                                    <div className={styles.featureIcon}>
                                        <img 
                                            src={features.content_features_item_3_icon || networkIcon} 
                                            alt={features.content_features_item_3_title} 
                                            width="74" 
                                            height="74" 
                                        />
                                    </div>
                                    <div className={styles.featureContent}>
                                        <Typography fontSize="20" fontWeight="900" color="dark" classes={styles.featureTitle}>
                                            {features.content_features_item_3_title}
                                        </Typography>
                                        <Typography fontSize="16" fontWeight="400" classes={styles.featureDescription}>
                                            {features.content_features_item_3_description}
                                        </Typography>
                                    </div>
                                </div>
                                <div className={styles.featureCard}>
                                    <div className={styles.featureIcon}>
                                        <img 
                                            src={features.content_features_item_4_icon || interfaceIcon} 
                                            alt={features.content_features_item_4_title} 
                                            width="74" 
                                            height="74" 
                                        />
                                    </div>
                                    <div className={styles.featureContent}>
                                        <Typography fontSize="20" fontWeight="900" color="dark" classes={styles.featureTitle}>
                                            {features.content_features_item_4_title}
                                        </Typography>
                                        <Typography fontSize="16" fontWeight="400" classes={styles.featureDescription}>
                                            {features.content_features_item_4_description}
                                        </Typography>
                                    </div>
                                </div>
                            </div>
                        </Container>
                    </section>
                )}

                {!isLoadingSpaces && spaces && spaces.data && spaces.data.length > 0 && (
                    <section className={styles.opportunitiesSection}>
                        <Container>
                            <div className={styles.sectionHeader}>
                                <Typography tagVariant={TypographyTagVariants.span} fontSize="12" fontWeight="400" classes={styles.sectionLabel}>
                                    {translations.highlights}
                                </Typography>
                                <Typography tagVariant={TypographyTagVariants.h2} fontSize="46" fontWeight="900">
                                    {translations.recentOpportunities}
                                </Typography>
                            </div>

                            <div className={styles.spacesGrid}>
                                {spaces.data.map((space) => {
                                    const firstGalleryImage = (space.galleries?.[0] as { url?: string } | undefined)?.url;

                                    return (
                                        <div key={space.uuid} className={styles.spaceCardWrapper}>
                                            <SpaceCard 
                                                uuid={space.uuid}
                                                name={space.title}
                                                address={space.address}
                                                price={parseFloat(space.price)}
                                                areaM2={space.areaM2}
                                                latitude={space.latitude}
                                                longitude={space.longitude}
                                                initialFavorite={space.like}
                                                image={firstGalleryImage || undefined}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </Container>
                    </section>
                )}

                <section className={styles.mapSection}>
                    <Container classes='px-0'>
                        {!bannerInfoLoading && bannerInfo && (
                            <MapHeader
                                title={bannerInfo.content_banner_info_title}
                                subtitle={bannerInfo.content_banner_info_subtitle}
                                description={bannerInfo.content_banner_info_description}
                            />
                        )}

                        <div className={styles.mapSearchBox}>
                            <div className={styles.toggleGroup}>
                                <ToggleButton
                                    name="mapSearchType"
                                    value="vender"
                                    label={translations.toggleSell}
                                    checked={mapSearchType === 'vender'}
                                    onChange={setMapSearchType}
                                />
                                <ToggleButton
                                    name="mapSearchType"
                                    value="arrendar"
                                    label={translations.toggleRent}
                                    checked={mapSearchType === 'arrendar'}
                                    onChange={setMapSearchType}
                                />
                            </div>

                            <div className={styles.selectWrapper}>
                                <Select
                                    type={SelectType.variant1}
                                    options={propertyTypeOptions}
                                    value={mapSpaceType}
                                    onChange={setMapSpaceType}
                                    classes={styles.mapSelectInput}
                                />
                            </div>

                            <Button
                                mode={ButtonMode.primary}
                                label={translations.search}
                                classes={styles.mapSearchButton}
                                onClick={handleMapSearch}
                            />
                        </div>
                    </Container>

                    <div className={styles.mapContainer}>
                        {coordinatesLoading && (
                            <div className={styles.mapLoadingOverlay}>
                                <span className={styles.mapLoadingSpinner} />
                                <span className={styles.mapLoadingText}>{translations.mapLoading}</span>
                            </div>
                        )}
                        <MapContainer
                            center={[38.5318719, -28.5308378]}
                            zoom={13}
                            scrollWheelZoom={false}
                            className={styles.leafletMap}>
                            <MapBoundsUpdater coordinates={coordinates} />
                            <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            {coordinates?.map((item) => {
                                const lat = parseFloat(item.latitude || '');
                                const lng = parseFloat(item.longitude || '');
                                if (!isNaN(lat) && !isNaN(lng)) {
                                    return (
                                        <Marker key={item.uuid} icon={pinIcon} position={[lat, lng]}>
                                            <span>{item.space_type}</span>
                                        </Marker>
                                    );
                                }
                                return null;
                            })}
                        </MapContainer>
                    </div>
                </section>

                {!bannerLoading && banner && (
                    <CtaBanner
                        variant="side"
                        subtitle={banner.content_banner_ready_to_boost_subtitle}
                        title={banner.content_banner_ready_to_boost_title || ''}
                        description={banner.content_banner_ready_to_boost_description}
                        buttonText={banner.content_banner_ready_to_boost_button_text}
                        onButtonClick={handleBannerClick}
                        imageUrl={banner.content_banner_ready_to_boost_image}
                        imageAlt={banner.content_banner_ready_to_boost_title || translations.bannerReadyToBoostAlt}
                    />
                )}
                <GallerySection />
            </div>
            <Footer mode="normal" />
        </>
    );
};
